import IntlMessages from '@app/components/utility/intlMessages';
import options from '@app/containers/NavMain/mainOptions';
import { getCompany } from '@app/helpers/utility';
import React, { useEffect, useState } from 'react';
import { FaChevronDown, FaTimes } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import { MobileMenuWrapper } from './MobileMenu.style';
import { NavigationIcon } from './navigationIcons';
import { getNavUrl, isNavActive, isNavParentActive } from './navUtils';

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
}

export const MobileMenu: React.FC<MobileMenuProps> = ({ open, onClose }) => {
  const { pathname } = useLocation();
  const company = getCompany();
  const [expanded, setExpanded] = useState<string | null>(null);

  // Close whenever the route changes
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Start with the current section expanded, lock page scroll, close on Escape
  useEffect(() => {
    if (!open) return;
    const current = options.find(
      (o: any) => o.children && isNavParentActive(pathname, o)
    );
    setExpanded(current ? current.key : null);

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <MobileMenuWrapper role="dialog" aria-modal="true" aria-label="Navigation">
      <div className="mobile-menu-header">
        <span className="mobile-menu-title">{company?.name}</span>
        <button
          type="button"
          className="mobile-menu-close"
          aria-label="Close menu"
          onClick={onClose}
        >
          <FaTimes />
        </button>
      </div>
      <nav className="mobile-menu-items">
        {options.map((option: any) => {
          const hasChildren = option.children && option.children.length > 0;
          const active = isNavParentActive(pathname, option);
          const icon = <NavigationIcon iconName={option.leftIcon} size={20} />;

          if (!hasChildren) {
            return (
              <Link
                key={option.key}
                to={getNavUrl(option)}
                className={`mobile-menu-item ${active ? 'active' : ''}`}
                onClick={onClose}
              >
                {icon}
                <IntlMessages id={option.label} />
              </Link>
            );
          }

          const isExpanded = expanded === option.key;
          return (
            <div key={option.key} className="mobile-menu-group">
              <button
                type="button"
                className={`mobile-menu-item ${active ? 'active' : ''}`}
                aria-expanded={isExpanded}
                onClick={() => setExpanded(isExpanded ? null : option.key)}
              >
                {icon}
                <IntlMessages id={option.label} />
                <FaChevronDown
                  className={`mobile-menu-chevron ${isExpanded ? 'open' : ''}`}
                />
              </button>
              {isExpanded && (
                <div className="mobile-menu-children">
                  {option.children.map((child: any) => (
                    <Link
                      key={child.key}
                      to={getNavUrl(child)}
                      className={`mobile-menu-child ${
                        isNavActive(pathname, child.key) ? 'active' : ''
                      }`}
                      onClick={onClose}
                    >
                      <IntlMessages id={child.label} />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </MobileMenuWrapper>
  );
};
